import { ButtonHTMLAttributes, MouseEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  BASIC_BLACK,
  BASIC_BROWN,
  BASIC_DISABLED,
  BASIC_LIGHT_BLUE,
  BASIC_WHITE,
  BASIC_YELLOW,
} from "../../constants/colors";
import { IconComponent } from "../../icons/types";

type Props = {
  text: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  btnStyle: "primary" | "secondary" | "disabled" | "payment";
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "full";
  rounded?: "xs" | "sm" | "md" | "lg";
  customSize?: { width: string; height: string };
  icon?: IconComponent;
};

export const Button: React.FC<Props> = ({
  text,
  type,
  btnStyle,
  onClick,
  disabled,
  size = "lg",
  rounded,
  customSize,
  icon: Icon,
}) => {
  const formContext = useFormContext();

  return (
    <StyledButton
      type={type}
      $btnStyle={btnStyle}
      onClick={onClick}
      size={size}
      $rounded={rounded}
      $customSize={customSize}
      disabled={
        type === "submit"
          ? !formContext?.formState.isValid ||
            formContext?.formState.isSubmitting
          : disabled
      }
    >
      {Icon && <Icon />}
      {text}
    </StyledButton>
  );
};

const StyledButton = styled.button<{
  $btnStyle: "primary" | "secondary" | "disabled" | "payment";
  size: "xxs" | "xs" | "sm" | "md" | "lg" | "full";
  $rounded?: "xs" | "sm" | "md" | "lg";
  $customSize?: { width: string; height: string };
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: ${({ $btnStyle }) =>
    $btnStyle === "primary"
      ? BASIC_YELLOW
      : $btnStyle === "secondary"
        ? BASIC_WHITE
        : $btnStyle === "payment"
          ? BASIC_LIGHT_BLUE
          : BASIC_DISABLED};
  border: ${({ $btnStyle }) =>
    $btnStyle === "secondary" ? `1px solid ${BASIC_BROWN}` : "none"};
  border-radius: ${({ $rounded }) =>
    $rounded === "xs"
      ? "4px"
      : $rounded === "sm"
        ? "6px"
        : $rounded === "md"
          ? "8px"
          : $rounded === "lg"
            ? "12px"
            : "0"};
  font-size: ${({ size }) => (size === "xxs" ? "12px" : "16px")};
  font-weight: 600;
  line-height: 1em;
  letter-spacing: 0;
  color: ${({ $btnStyle }) =>
    $btnStyle === "disabled"
      ? BASIC_BLACK
      : $btnStyle === "payment"
        ? BASIC_WHITE
        : BASIC_BROWN};
  padding: ${({ size }) =>
    size === "lg"
      ? "20px 0"
      : size === "xxs"
        ? "10px 5px"
        : size === "xs"
          ? "10px"
          : "15px 0"};
  width: ${({ $customSize, size }) =>
    $customSize?.width ||
    (size === "full"
      ? "100%"
      : size === "lg"
        ? "300px"
        : size === "md"
          ? "200px"
          : size === "sm"
            ? "100px"
            : size === "xs"
              ? "90px"
              : "70px")};
  height: ${({ $customSize, size }) =>
    $customSize?.height || (size === "lg" ? "60px" : "auto")};
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.5);
  &:focus {
    outline: none;
  }
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $btnStyle }) => ($btnStyle === "disabled" ? 1 : 0.3)};
  }
`;
